import { isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID, signal } from "@angular/core";
import { JobDetails } from '../models/details.model';

@Injectable({
    providedIn: 'root'
})

export class DetailsUtilService {
    private platformId = inject(PLATFORM_ID)
    private isBrowser = signal(isPlatformBrowser(this.platformId));

    socialMediaShare(url:string, PostUrl:string, message?:string){
        if(this.isBrowser()){
            const jobUrl = encodeURIComponent(PostUrl)
            const msg = encodeURIComponent(message ?? '') 
            const shareableUrl = msg ? `${url}${msg} ${jobUrl}` : `${url}${jobUrl}`
            window.open(shareableUrl, '_blank','width=800,height=600')
        }
    }

    triggerPrint(): void {
        if(this.isBrowser()){
          const printUrl = document.getElementById("print-url");
    
          if (printUrl) {
            printUrl.innerText = `URL: ${window.location.href}`;
          }
    
          window.print();
        }
        
    }

    fixSingleAnchor(html: string): string {
      if (this.isBrowser()) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const anchor = doc.querySelector('a');
  
        if (anchor) {
          let href = anchor.getAttribute('href') || '';
          const text = anchor.textContent || '';
  
          if (!href.startsWith('http://') && !href.startsWith('https://')) {
            href = 'http://' + href;
            anchor.setAttribute('href', href);
          }
  
          if (!text.startsWith('http://') && !text.startsWith('https://')) {
            anchor.textContent = href;
          }
  
          return doc.body.innerHTML;
        }
  
        return html;
      }
      return html;
    }

    formatJobLocation(location: string | undefined): {
      display: string;
      full: string;
      showTooltip: boolean;
    } {
      if (!location) return { display: '', full: '', showTooltip: false };
  
      const parts = location.split(',').map(part => part.trim());
  
      if (parts.length <= 2) {
        return { display: location, full: location, showTooltip: false };
      }
  
      return {
        display: `${parts[0]}, ${parts[1]}...`,
        full: location,
        showTooltip: true
      };
    }

    getFormattedList(educationReq:string): string[] {
      const education = educationReq
      if(this.isBrowser()){
        const tempDiv = document.createElement('div');
      try {
        tempDiv.innerHTML = education|| '';
      } catch (e) {
        console.error('Invalid HTML in education field', e);
        return [];
      }
    
      const resultSet = new Set<string>();
    
    
      const allListItems = tempDiv.querySelectorAll('li');
      allListItems.forEach(li => {
        const text = li.textContent?.trim();
        if (text) resultSet.add(text);
      });
    
      const blockTextTags = Array.from(
        tempDiv.querySelectorAll('p, div, span, section, article')
      ).filter(el => !el.closest('li')); 
    
      blockTextTags.forEach(el => {
        const text = el.textContent?.trim();
        if (text) resultSet.add(text);
      });
    
      const directText = tempDiv.childNodes;
      directText.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) resultSet.add(text);
        }
      });
    
      return Array.from(resultSet);
      }
    
      return []
      
    }

    processStringProperties(obj: JobDetails): JobDetails {
      const excludedKeys = ['experience', 'JobOtherBenifits', 'EducationRequirements'];
      if (obj && typeof obj === 'object') {
        for (const key in obj) {
          if (obj.hasOwnProperty(key) && typeof obj[key] === 'string') {
  
            if (!excludedKeys.includes(key)) {
              obj[key] = obj[key].replace(/<ul>/g, '<ul class="list-disc pl-5 md:px-6">');
              obj[key] = obj[key].replace(/<ol>/g, '<ol class="list-decimal pl-5 md:px-6">')
              obj[key] = obj[key].replace(/<a /g, '<a class="text-[#0E73A9] font-medium"')
  
              // if (key === 'AdditionJobRequirements') {
              //   obj[key] = obj[key].replace(/<li>Only Male<\/li>/g, '');
              //   obj[key] = obj[key].replace(/<li>Only Female<\/li>/g, '');
              // }
  
  
            }
            if (excludedKeys.includes(key)) {
              obj[key] = obj[key].replace(/style=['"][^'"]*['"]/g, '');
            }
            obj[key] = obj[key].replace(/<%=(.*?)%>/g, '$1');
  
  
          }
  
  
        }
      }
      return obj;
    }

    getTextSegments(applyEmail: string | null): { strongText: string; emailText: string } {
      if (!applyEmail) {
        return { strongText: '', emailText: '' };
      }
  
      let emailText = applyEmail.trim();
      let strongText = '';
  
      const strongMatch = applyEmail.match(/<strong>(.*?)<\/strong>/);
      if (strongMatch) {
        strongText = strongMatch[1].trim();
      }
  
  
      const orIndex = applyEmail.toLowerCase().indexOf(' or ');
      if (orIndex !== -1) {
        emailText = applyEmail.substring(0, orIndex).trim();
      }
  
      return { strongText, emailText };
    }


    getFormattedBenefit(benefit:string): string[] {
      if (!this.isBrowser()) {
        return [benefit];
      }
      const benefits = benefit
      if (!this.isBrowser || !benefits) return [];
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = benefits;
  
      tempDiv.querySelectorAll('ul').forEach(ul => {
        ul.replaceWith(...Array.from(ul.childNodes));
      });
  
      return Array.from(tempDiv.childNodes)
        .map(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            return (node as HTMLElement).outerHTML.trim();
          } else {
  
            return node.textContent?.trim() ?? '';
          }
        })
        .filter(text => text !== '');
    }

    containsListTag(benefits:string): boolean {
      return this.getFormattedBenefit(benefits).some(item => item.includes('<li>'));
    }
    containsPTag(benefits:string): boolean {
      if (this.isBrowser()) {
        return this.getFormattedBenefit(benefits).some(item => item.includes('<p>'));
      }

      return false;
    }

    processLinkStyle(html: string): string {
      if (!html) return '';
      const updatedHtml = html.replace(
        /<a\s/gi,
        '<a class="hover:text-[#10557C ] text-[#0E73A9]" '
      );
      return updatedHtml
  
    }

  splitExperience(experience: string): { experiences: string; additionalExperience: string } {
    if (!this.isBrowser() || experience.length === 0) {
      return { experiences: '', additionalExperience: '' };
    }

    const parser = new DOMParser();
    const exp = parser.parseFromString(experience, 'text/html');
    const container = exp.querySelector('ul, ol') ?? exp.body;

    const items: string[] = [];
    container.childNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.tagName === 'LI' || el.tagName === 'P') {
          const text = el.innerHTML.trim();
          if (text.length > 0) {
            items.push(text);
          }
        }
      } else if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim() ?? '';
        if (text.length > 0) {
          items.push(text);
        }
      }
    });

    if (items.length === 0) {
      return { experiences: '', additionalExperience: '' };
    }

    const experiences = `<ul><li>${items[0]}</li></ul>`;
    const remainingItems = items.slice(1).map(item => `<li>${item}</li>`).join('');
    const additionalExperience = remainingItems.length > 0
      ? `<ul class="list-disc px-5">${remainingItems}</ul>`
      : '';

    return { experiences, additionalExperience };
  }

  getGender(genderValue: string): string {
    switch (genderValue) {
      case 'M':
        return 'onlyMale';
      case 'F':
        return 'onlyFemale';
      case '0':
        return 'onlyThirdGender';
      case 'M,F':
      case 'M,F,O':
      case 'M,O':
      case 'F,O':
      case 'B':
        return '';
      default:
        return genderValue ?? '';
    }
  }

    getSections(conditions: {
      isRequirements: boolean;
      isResponsibilities: boolean;
      isSkill: boolean;
      isSalary: boolean;
    }) {
      return [
        { id: 'allSection', label: 'All', condition: true },
        { id: 'requirements', label: 'Requirements', condition: conditions.isRequirements },
        { id: 'responsibilitiesSection', label: 'Responsibilities', condition: conditions.isResponsibilities },
        { id: 'skills', label: 'Skills & Experties', condition: conditions.isSkill },
        { id: 'salary', label: 'Salary & Benefits', condition: conditions.isSalary },
        { id: 'companyInfo', label: 'Company Information', condition: true },
      ];
    }
}